import { type AssistantStatus } from "@cloudserver/contracts";
import clsx from "clsx";
import { Bot } from "lucide-react";
import { useCallback, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ApiProblem } from "../api/client.js";
import {
  levelRank,
  putSessionLevel,
  sessionLevel,
  terminalAccessOf,
  TERMINAL_ACCESS_LEVELS,
  type TerminalAccessLevel,
} from "../assistant/access.js";
import { useAssistantStatus } from "../assistant/api.js";
import { StepUpCancelled, useStepUpGuard } from "../pages/users/step-up.js";
import { problemText } from "../ui/feedback.js";
import { useToast } from "../ui/toast.js";
import { kindOf, terminals, type TerminalTab } from "./store.js";

/*
 * The Autopilot's access to a terminal session (docs/terminal.md, "Working
 * together with the assistant"; owner's design of 2026-10-22): four levels —
 * «Выключено», «Только чтение», «Модерация изменений», «Авто» — chosen per
 * session in the chat's line under the input or in the tab's right-click
 * menu, both reading the same tab state. Every rise from «Выключено» asks a
 * fresh second factor; changes between the levels above it do not. The tab carries the Autopilot's mark, the screen a
 * soft glow, while it has access. The API ends it after 30 minutes without
 * activity, on Ctrl+C, «Стоп» or when the tab closes; its frames keep
 * `tab.pair` true.
 */

/** Whether the Autopilot can be given access to this tab: a connected SSH session, the assistant on. */
export function mayPair(
  tab: TerminalTab | undefined,
  status: AssistantStatus | undefined,
): boolean {
  if (!tab || !status) return false;
  const projectOn = status.allProjects || status.projectIds.includes(tab.projectId);
  return (
    kindOf(tab) === "ssh" &&
    tab.status === "connected" &&
    tab.sessionId !== null &&
    status.available &&
    status.enabled &&
    projectOn &&
    status.changes.terminalAccessMax !== "off"
  );
}

/** Sets the session's level through the API; the tab follows (and the API's frames after). */
export async function setSessionLevel(tab: TerminalTab, level: TerminalAccessLevel): Promise<void> {
  const now = await putSessionLevel(tab, level);
  terminals.update(tab.id, {
    pair: {
      on: now.on,
      level: now.level,
      command: null,
      reason: now.on ? null : "person",
    },
  });
}

/** One level of the choice: whether it can be chosen for this tab, and why not. */
export interface LevelChoice {
  level: TerminalAccessLevel;
  /** Above the organisation's maximum, or the tab cannot have access at all. */
  disabled: boolean;
}

/** The four levels for a tab, those above what the organisation allows disabled. */
export function levelChoices(
  tab: TerminalTab | undefined,
  status: AssistantStatus | undefined,
): LevelChoice[] {
  const allowed = mayPair(tab, status);
  const max = terminalAccessOf(status).orgMax;
  return TERMINAL_ACCESS_LEVELS.map((level) => ({
    level,
    disabled: level !== "off" && (!allowed || levelRank(level) > levelRank(max)),
  }));
}

/**
 * Choosing a session's level, with the second factor when the API asks for
 * it (render `dialog` once); a refusal shows as a toast. Resolves true once
 * the level is set, false when it was refused or the second factor cancelled.
 */
export function useSessionAccess(): {
  choose: (tab: TerminalTab, level: TerminalAccessLevel) => Promise<boolean>;
  dialog: ReactNode;
  working: boolean;
} {
  const { t } = useTranslation();
  const toast = useToast();
  const { guard, dialog } = useStepUpGuard();
  const [working, setWorking] = useState(false);
  const choose = useCallback(
    async (tab: TerminalTab, level: TerminalAccessLevel) => {
      setWorking(true);
      try {
        await guard(() => setSessionLevel(tab, level));
        return true;
      } catch (error) {
        if (error instanceof StepUpCancelled) return false;
        toast({
          tone: "danger",
          title:
            error instanceof ApiProblem && error.problem
              ? problemText(t, error.problem)
              : t("common.unexpected"),
        });
        return false;
      } finally {
        setWorking(false);
      }
    },
    [guard, t, toast],
  );
  return { choose, dialog, working };
}

/** On a tab while the Autopilot has access: its mark; it pulses while a command runs. */
export function PairBadge({ tab, className }: { tab: TerminalTab; className?: string }) {
  const { t } = useTranslation();
  if (!tab.pair?.on) return null;
  const label = tab.pair.command
    ? t("sessions.pair.running", { command: tab.pair.command })
    : t("sessions.pair.on");
  return (
    <span
      data-pair={tab.pair.command ? "running" : "on"}
      title={label}
      className={clsx(
        "flex shrink-0 items-center rounded-full bg-accent px-1 py-0.5 text-accent-fg",
        tab.pair.command && "motion-safe:animate-pulse",
        className,
      )}
    >
      <Bot className="size-3" aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  );
}

/**
 * The soft blue glow around a terminal's screen while the Autopilot has
 * access (owner's request of 2026-10-22): an inset shadow over the pane that
 * takes no room (the terminal is not refitted) and catches no pointer; it
 * pulses slowly only while a command runs, and not at all with reduced
 * motion. Screen readers get the level in words.
 */
export function AgentGlow({ tab }: { tab: TerminalTab }) {
  const { t } = useTranslation();
  const status = useAssistantStatus();
  const level = sessionLevel(tab, status.data);
  if (level === "off") return null;
  return (
    <>
      <div
        aria-hidden
        data-agent-glow={level}
        data-running={tab.pair?.command ? "" : undefined}
        className="agent-glow pointer-events-none absolute inset-0 z-10"
      />
      <span className="sr-only">
        {t("sessions.pair.access", { level: t(`assistant.access.levels.${level}`) })}
      </span>
    </>
  );
}
